import React, { useState } from "react"
import Header from "../components/Header"
import Footer from "../components/Footer"

const sections = [
  {
    id: "overview",
    title: "Overview",
    content: `resolut.co.in and InterviewGuard are committed to your privacy. This policy covers both the resolut.co.in platform and the InterviewGuard desktop application ("the App") — a session monitoring tool candidates install during technical interviews.

InterviewGuard is designed with a privacy-first architecture: it runs entirely on your local machine, communicates only with resolut.co.in servers, and never captures or transmits your screen content.`
  },
  {
    id: "what-we-collect",
    title: "What We Collect",
    subsections: [
      {
        title: "resolut.co.in platform",
        items: [
          "Account information (name, email, organisation)",
          "Interview session metadata (timestamps, job role, interview results)",
          "Usage data (pages visited, features used)",
          "Communications sent through the platform"
        ]
      },
      {
        title: "InterviewGuard desktop app",
        items: [
          "Names and bundle identifiers of flagged processes (AI cheat tools detected during a session)",
          "Whether hidden screen-capture overlays were detected",
          "Session ID and timestamp of each scan result",
          "Device token — a randomly generated identifier stored locally on your machine used to associate scan results with your interview session"
        ]
      }
    ]
  },
  {
    id: "what-we-dont-collect",
    title: "What We Do NOT Collect",
    highlight: true,
    items: [
      "Your screen content, keystrokes, or webcam footage",
      "Passwords, browser history, or cookies",
      "Personal files or documents",
      "Any data from apps that are not on our known AI cheat tool list",
      "Audio or microphone input",
      "Location data"
    ]
  },
  {
    id: "how-app-works",
    title: "How InterviewGuard Works",
    content: `InterviewGuard is a macOS and Windows desktop application that runs a local HTTP server on port 7734 (127.0.0.1 only — never accessible from outside your machine). When an interview session starts, the resolut.co.in platform communicates with the app through this local server.

The app scans for:`,
    items: [
      "Running processes that match a known list of AI interview assistant tools (e.g. Cluely, Final Round AI, ParakeetAI)",
      "Windows that are hidden from screen capture — a technique used by overlay-based cheat tools"
    ],
    contentAfter: `These checks run every 30 seconds during an active session. Results are sent to resolut.co.in servers to inform the interviewer. The app terminates automatically when the session ends.

Screen Recording permission on macOS is used solely to enumerate window metadata (owner, position, capture state) — no pixel content of your screen is ever read, stored, or transmitted.`
  },
  {
    id: "how-we-use",
    title: "How We Use Your Data",
    items: [
      "To conduct and evaluate technical interviews on the resolut.co.in platform",
      "To detect the use of AI-assisted cheating tools during live interview sessions",
      "To provide interviewers with integrity reports for each session",
      "To improve detection accuracy and reduce false positives over time",
      "To comply with legal obligations"
    ]
  },
  {
    id: "google-calendar",
    title: "Google Calendar Integration",
    content: "Recruiters may optionally connect their personal Google Calendar to Resolut. When connected, Resolut requests access to the following Google API scope:",
    items: [
      "calendar.events — used solely to create interview events in the recruiter's calendar with a Google Meet link, and to invite the candidate and interviewer as attendees"
    ],
    contentAfter: `We do not read, list, modify, share, or delete any existing calendar events. We do not access any other Google account data.

Resolut's use of Google Calendar data is limited to creating interview events on behalf of the recruiter who explicitly connected their account. Calendar access tokens are stored securely and used only to perform this action.

Recruiters can disconnect their Google Calendar at any time from Settings → Integrations, which immediately revokes our access and deletes the stored tokens.

Resolut's use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy, including the Limited Use requirements.`
  },
  {
    id: "data-sharing",
    title: "Data Sharing",
    content: "We do not sell your personal data. We may share data with:",
    items: [
      "Hiring organisations using resolut.co.in — they receive interview results and integrity reports",
      "Service providers (hosting, analytics) under strict data processing agreements",
      "Law enforcement when required by law"
    ]
  },
  {
    id: "retention",
    title: "Data Retention",
    items: [
      "Interview session data and scan results: retained for 12 months",
      "Account data: retained while your account is active",
      "Device token: stored locally on your machine only, never on our servers in plain form",
      "You may request deletion at any time by contacting us"
    ]
  },
  {
    id: "rights",
    title: "Your Rights",
    content: "Depending on your jurisdiction you may have the right to:",
    items: [
      "Access the personal data we hold about you",
      "Request correction of inaccurate data",
      "Request deletion of your data",
      "Object to or restrict processing",
      "Data portability",
      "Withdraw consent at any time"
    ],
    contentAfter: "To exercise any of these rights contact us at privacy@resolut.co.in."
  },
  {
    id: "security",
    title: "Security",
    content: "We implement industry-standard security measures including TLS encryption for data in transit, access controls, and regular security reviews. The InterviewGuard app communicates with our servers over HTTPS only. The local HTTP server on your machine is bound to 127.0.0.1 and is not accessible from the internet or local network."
  },
  {
    id: "consent",
    title: "Consent & Installation",
    content: "InterviewGuard is installed voluntarily by candidates prior to their interview. On first launch the app presents a clear consent screen explaining exactly what it monitors and what it does not. Candidates who decline are not monitored. Continued use of the app during an interview session constitutes consent to the data practices described in this policy."
  },
  {
    id: "contact",
    title: "Contact Us",
    content: "For privacy questions or to exercise your rights:",
    contact: {
      email: "privacy@resolut.co.in",
      website: "resolut.co.in"
    }
  }
]

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null)

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">

        {/* Header */}
        <div className="mb-12 sm:mb-16">
          <p className="text-xs font-semibold tracking-widest uppercase text-amber-600 mb-3">
            Legal
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-gray-900 mb-4">
            Privacy Policy
          </h1>
          <p className="text-base text-gray-500 font-light">
            Effective date: March 17, 2026 &nbsp;·&nbsp; Applies to resolut.co.in and InterviewGuard
          </p>
        </div>

        <div className="lg:grid lg:grid-cols-12 lg:gap-12">

          {/* Sticky sidebar nav */}
          <aside className="hidden lg:block lg:col-span-3">
            <div className="sticky top-8">
              <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4">
                Contents
              </p>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <a
                    key={s.id}
                    href={`#${s.id}`}
                    className="block text-sm text-gray-500 hover:text-amber-600 py-1 border-l-2 border-transparent hover:border-amber-500 pl-3 transition-all duration-150"
                  >
                    {s.title}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="lg:col-span-9 space-y-10">
            {sections.map((section) => (
              <section key={section.id} id={section.id}
                className="pb-10 border-b border-dashed border-gray-200 last:border-0"
              >
                <h2 className="text-lg font-medium text-gray-900 mb-4">
                  {section.title}
                </h2>

                {section.content && (
                  <p className="text-sm text-gray-600 leading-relaxed mb-4 whitespace-pre-line">
                    {section.content}
                  </p>
                )}

                {/* Highlight box for "what we don't collect" */}
                {section.highlight && section.items && (
                  <div className="border border-dashed border-amber-300 bg-amber-50 p-5 rounded-xl">
                    <ul className="space-y-2">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2.5 text-sm text-amber-900">
                          <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Regular items */}
                {!section.highlight && section.items && (
                  <ul className="space-y-2 mb-4">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-gray-600">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {/* Subsections */}
                {section.subsections && (
                  <div className="space-y-5">
                    {section.subsections.map((sub, i) => (
                      <div key={i}>
                        <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-2">
                          {sub.title}
                        </p>
                        <ul className="space-y-2">
                          {sub.items.map((item, j) => (
                            <li key={j} className="flex items-start gap-2.5 text-sm text-gray-600">
                              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-gray-300 flex-shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {section.contentAfter && (
                  <p className="text-sm text-gray-600 leading-relaxed mt-4">
                    {section.contentAfter}
                  </p>
                )}

                {/* Contact block */}
                {section.contact && (
                  <div className="mt-4 inline-flex flex-col gap-1">
                    <a href={`mailto:${section.contact.email}`}
                      className="text-sm text-amber-600 hover:underline">
                      {section.contact.email}
                    </a>
                    <a href={`https://${section.contact.website}`}
                      className="text-sm text-amber-600 hover:underline">
                      {section.contact.website}
                    </a>
                  </div>
                )}
              </section>
            ))}

            {/* Footer note */}
            <p className="text-xs text-gray-400 leading-relaxed pt-2">
              This policy may be updated from time to time. We will notify you of material changes via email or a notice on resolut.co.in. Continued use of our services after changes constitutes acceptance of the updated policy.
            </p>
          </main>
        </div>
      </div>

      <Footer />
    </div>
  )
}

export default PrivacyPolicy